/**
 * Company Name Utilities
 * 
 * Functions for processing and displaying company names with suffixes
 */

/**
 * Extracts the company suffix (form type) from a company name
 * Returns an object with the main name and the suffix
 */
export function extractCompanySuffix(name: string): { mainName: string; suffix: string } {
  if (!name || typeof name !== 'string') {
    return { mainName: name || '', suffix: '' };
  }

  const trimmed = name.trim();
  
  // Common company form suffixes (Finnish, Swedish, International)
  // Order matters: longer patterns first to avoid partial matches
  const companyFormPatterns = [
    // Full forms (longest first)
    /\s+(Julkinen osakeyhtiö|Kommandiittiyhtiö|Osakeyhtiö|Toiminimi)\s*$/i,
    /\s+(Publikt aktiebolag|Kommanditbolag|Handelsbolag|Aktiebolag|Företagsnamn)\s*$/i,
    // Combined forms with multiple suffixes (e.g., "Corporation Ab", "Corporation Ltd")
    /\s+(Corporation|Incorporated|Limited)\s+(Ab\.|Ab|Ltd\.|Ltd|Inc\.|Inc|LLC|L\.L\.C\.|L\.L\.C|Oy|Oyj|Plc|Plc\.)\s*$/i,
    // Combined Finnish/Swedish forms
    /\s+(Oy Ab\.|Oy Ab|Oyj Abp|Oyj Ab\.|Publ AB\.|Publ AB|Publ\. AB\.|Publ\. AB)\s*$/i,
    // Individual abbreviations
    /\s+(Oyj|Tmi\.|Tmi|Ky\.|Ky|Osk\.|Osk|Oy|KB\.|KB|HB\.|HB|Ab\.|Ab|Abp\.|Abp)\s*$/i,
    /\s+(Incorporated|Corporation|Limited|L\.L\.C\.|L\.L\.C|LLC|Ltd\.|Ltd|Inc\.|Inc|Corp\.|Corp|Plc|Plc\.)\s*$/i,
    // Country names as company form suffixes
    /\s+(Suomi|Finland)\s*$/i,
    // Also match without word boundary for abbreviations
    /\b(oy|ab|ltd|oyj|oy ab|oyj abp|abp|osakeyhtiö|aktiebolag|inc|llc|plc|ky|kb|ay|tmi|toiminimi|aktiebolaget|aktiebolag ab|aktiebolaget ab|aktiebolag oy|aktiebolaget oy|aktiebolag ltd|aktiebolaget ltd|aktiebolag oyj|aktiebolaget oyj|aktiebolag ky|aktiebolaget ky|aktiebolag kb|aktiebolaget kb|aktiebolag ay|aktiebolaget ay|aktiebolag tmi|aktiebolaget tmi|suomi|finland)\.?$/i,
  ];

  for (const pattern of companyFormPatterns) {
    const match = trimmed.match(pattern);
    if (match) {
      const suffix = match[0].trim();
      const mainName = trimmed.replace(pattern, '').trim();
      // Capitalise 'Kaupunki' if present (case-insensitive)
      const cleanedMainName = mainName.replace(/kaupunki/gi, (m) => 
        m.charAt(0).toUpperCase() + m.slice(1).toLowerCase()
      );
      return { mainName: cleanedMainName, suffix };
    }
  }

  // No suffix found, return the name as-is (with Kaupunki capitalization)
  const cleaned = trimmed.replace(/kaupunki/gi, (match) => 
    match.charAt(0).toUpperCase() + match.slice(1).toLowerCase()
  );
  return { mainName: cleaned, suffix: '' };
}

/**
 * Properly capitalizes a company name to title case.
 * Capitalizes the first letter of each word, lowercases the rest.
 * Handles special cases like "Kaupunki" and preserves existing capitalization patterns.
 */
export function capitalizeCompanyName(name: string): string {
  if (!name || typeof name !== 'string') return name || '';
  
  return name
    .trim()
    .split(/\s+/)
    .map((word) => {
      if (!word) return word;
      // Capitalize first letter, lowercase the rest
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Strips company type suffixes from a company name (legacy function for backward compatibility)
 * @deprecated Use extractCompanySuffix instead for display purposes
 */
export function stripCompanyType(name: string): string {
  const { mainName } = extractCompanySuffix(name);
  return mainName;
}

/**
 * Trailing words that describe a company's structure or geography rather than its brand. On
 * compact surfaces (SRP cards) these move into the muted suffix next to the legal form, so
 * "Wärtsilä Finland Oy" reads as "Wärtsilä" + "Finland Oy" and "Posti Group Oyj" as "Posti" +
 * "Group Oyj". Display only: the stored name, slugs and search keep the full name.
 */
const NON_BRAND_TRAILING_TERMS = [
  // Corporate structure
  'Group', 'Groups', 'Holding', 'Holdings', 'Konserni', 'Koncern', 'Koncernen', 'Yhtymä',
  'Sivuliike', 'Suomen sivuliike', 'Filial', 'Filial i Finland', 'Branch', 'Finland Branch',
  'Branch in Finland', 'Finnish Branch',
  // Countries and wider regions
  'Finland', 'Suomi', 'Nordic', 'Nordics', 'Scandinavia', 'Europe', 'EMEA',
  // Finnish macro-regions
  'Etelä-Suomi', 'Länsi-Suomi', 'Itä-Suomi', 'Pohjois-Suomi', 'Södra Finland', 'Västra Finland',
  'Östra Finland', 'Norra Finland',
  // Regions (maakunnat / landskap), fi and sv
  'Uusimaa', 'Länsi-Uusimaa', 'Itä-Uusimaa', 'Nyland', 'Västra Nyland', 'Östra Nyland',
  'Varsinais-Suomi', 'Egentliga Finland', 'Satakunta', 'Kanta-Häme', 'Egentliga Tavastland',
  'Pirkanmaa', 'Birkaland', 'Päijät-Häme', 'Päijänne-Tavastland', 'Kymenlaakso', 'Kymmenedalen',
  'Etelä-Karjala', 'Södra Karelen', 'Etelä-Savo', 'Södra Savolax', 'Pohjois-Savo',
  'Norra Savolax', 'Pohjois-Karjala', 'Norra Karelen', 'Keski-Suomi', 'Mellersta Finland',
  'Etelä-Pohjanmaa', 'Södra Österbotten', 'Pohjanmaa', 'Österbotten', 'Keski-Pohjanmaa',
  'Mellersta Österbotten', 'Pohjois-Pohjanmaa', 'Norra Österbotten', 'Kainuu', 'Kajanaland',
  'Lappi', 'Lappland', 'Ahvenanmaa', 'Åland',
];

// Longest first so "Filial i Finland" wins over "Finland", "Pohjois-Pohjanmaa" over "Pohjanmaa".
const NON_BRAND_TRAILING_PATTERN = new RegExp(
  `[\\s,]+(?:${[...NON_BRAND_TRAILING_TERMS]
    .sort((a, b) => b.length - a.length)
    .map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|')})\\.?\\s*$`,
  'iu',
);

/** Names where the country is the brand itself; never split these. */
const COUNTRY_IS_BRAND = new Set(['business finland', 'visit finland', 'team finland']);

/**
 * Like {@link extractCompanySuffix}, but keeps peeling trailing legal forms AND non-brand
 * descriptors (Group, Finland, a region name...) until only the brand is left. The peeled part
 * is returned verbatim as `suffix` (e.g. "Pohjanmaa Oy"). Always leaves at least one word.
 */
export function extractCompanyBrand(name: string): { mainName: string; suffix: string } {
  const trimmed = (name || '').trim();
  if (!trimmed) return { mainName: '', suffix: '' };

  let main = trimmed;
  for (;;) {
    if (COUNTRY_IS_BRAND.has(main.toLowerCase())) break;
    const descriptor = main.match(NON_BRAND_TRAILING_PATTERN);
    const next = descriptor
      ? main.slice(0, descriptor.index).trim()
      : extractCompanySuffix(main).mainName;
    if (!next || next === main || !/[\p{L}\p{N}]/u.test(next)) break;
    main = next;
  }

  // The suffix is whatever followed the brand in the original string, so casing and order
  // survive ("Finland Oy", not "Oy Finland"). extractCompanySuffix may recapitalise "Kaupunki",
  // so locate the cut by length rather than by searching for `main`, then reapply that casing.
  const suffix = trimmed.slice(main.length).replace(/^[\s,]+/, '').trim();
  const mainName = main
    .replace(/,\s*$/, '')
    .replace(/kaupunki/gi, (m) => m.charAt(0).toUpperCase() + m.slice(1).toLowerCase());
  return { mainName, suffix };
}
